import Store from '@ember-data/store';
import { assert } from '@ember/debug';
import type ModelRegistry from 'ember-data/types/registries/model';
import { Promise } from 'rsvp';

const memoryCache: { [cacheKey: string]: string } = {};

// args:
// - store
// - modelName
// - queryOptions
// - pageSize - default to 50
// - pageCallback - function that is called after every page of records are downloaded
//   - function({ pageRecords, downloadedCount, totalCount})
//
// Returns:
// promise that resolves with array of all records
//
// It's expecting the server response to include meta.total_count = total number of records
export function queryWithPages<T>(args: {
  store: Store;
  modelName: string;
  queryOptions?: any;
  pageSize?: number;
  pageCallback?: (args: {
    pageRecords: T[];
    downloadedCount: number;
    totalCount: number;
  }) => void;
}): Promise<T[]> {
  let pageSize = args.pageSize ?? 50;
  let queryOptions = args.queryOptions ?? {};
  queryOptions.filter = queryOptions.filter ?? {};
  queryOptions.filter.limit = pageSize;
  return new Promise<T[]>((resolve, reject) => {
    queryPage<T>({
      promiseResolve: resolve,
      promiseReject: reject,
      store: args.store,
      modelName: args.modelName,
      queryOptions,
      records: [],
      page: 1,
      pageSize,
      pageCallback: args.pageCallback,
    });
  });
}

// Uses memory and localStorage to cache records from the server.
//
// If the records are already cached in memory, it returns immediately and records are NOT retrieved from the server
//
// If the records are not cached in memory but in localStorage, it:
//  - saves the records in the memory cache
//  - returns immediately with the cached records
//  - retrieves fresh records from the server and calls 'onRecordsFromServer' when the fresh records come back from the server
//  - it saves the fresh records from the server in both memory and localStorage cache
//
// args:
// - cacheKey - the cache key to use. Make sure it's unique between different funds
// - cacheLocalWhenRecordLengthGreaterThan - when record length is greater, then results are cached in local storage
// - onRecordsFromServer - this is called with the fresh records returned from the server
//   - not called if there are no cached records
// - the rest of the args are the same as `queryWithPages` above
//
// Returns:
// - when cached values exist, it returns the cached values
// - when cached values don't exist, it returns the records from the server
export function queryWithPagesAndLocalCache<T>(args: {
  cacheKey: string;
  cacheLocalWhenRecordLengthGreaterThan: number;
  store: Store;
  modelName: string;
  onRecordsFromServer: (args: { records: T[] }) => void;
  queryOptions?: any;
  pageSize?: number;
  pageCallback?: (args: {
    pageRecords: T[];
    downloadedCount: number;
    totalCount: number;
  }) => void;
}): Promise<T[]> {
  // remove old cached queries as we move to CachedDataset
  cleanupQueryCache('store-utils-');

  let promise: Promise<T[]> | undefined;
  let serializedData: string | undefined | null;

  const cacheKey = args.cacheKey;

  serializedData = memoryCache[cacheKey];
  let hasMemoryCachedRecords = serializedData != null;

  if (!hasMemoryCachedRecords) {
    serializedData = localStorage.getItem(cacheKey);
  }
  let hasCachedRecords = serializedData != null;

  // if the records have already been cached, then resolve the promise immediately with the cached records
  if (hasCachedRecords) {
    try {
      let serializedArray = JSON.parse(serializedData!) as string[];
      let records: T[] = [];
      serializedArray.forEach((data: any) => {
        let record = args.store.push(
          args.store.normalize(args.modelName, data.data),
        ) as unknown;
        records.push(record as T);
      });
      promise = new Promise<T[]>((resolve) => {
        resolve(records);
      });
    } catch (e) {
      hasCachedRecords = false;
    }
  }

  if (!hasMemoryCachedRecords) {
    // now get the fresh records from the server
    let serverPromise = queryWithPages<T>({
      store: args.store,
      modelName: args.modelName,
      queryOptions: args.queryOptions,
      pageSize: args.pageSize,
      pageCallback: hasCachedRecords ? undefined : args.pageCallback,
    }).then((serverRecords) => {
      // always cache the fresh server records in memory
      memoryCache[cacheKey] = JSON.stringify(
        serverRecords.map((x: any) => {
          let serialized = x.serialize({ includeId: true });
          serialized.data.type = args.modelName;
          return serialized;
        }),
      );

      if (serverRecords.length > args.cacheLocalWhenRecordLengthGreaterThan) {
        try {
          localStorage.setItem(cacheKey, memoryCache[cacheKey]);
        } catch (e) {
          // do nothing
        }
      } else {
        try {
          localStorage.removeItem(cacheKey);
        } catch (e) {
          // do nothing
        }
      }

      if (hasCachedRecords) {
        args.onRecordsFromServer({ records: serverRecords });
      }

      return serverRecords;
    }) as Promise<T[]>;

    if (!hasCachedRecords) {
      promise = serverPromise;
    }
  }

  return promise!;
}

function queryPage<T>(args: {
  promiseResolve: (records: T[]) => void;
  promiseReject: (error: any) => void;
  store: Store;
  modelName: string;
  queryOptions: any;
  records: T[];
  page: number;
  pageSize: number;
  pageCallback?: (args: {
    pageRecords: T[];
    downloadedCount: number;
    totalCount: number;
  }) => void;
}) {
  args.queryOptions.filter.offset = (args.page - 1) * args.pageSize;
  args.store.query(args.modelName, args.queryOptions).then(
    (results) => {
      let totalCount = (results as any)['meta']?.total_count;
      assert(
        `${args.modelName} query is expected to return meta.total_count`,
        totalCount != null,
      );

      let pageRecords = results.slice() as T[];
      args.records.push(...pageRecords);
      args.pageCallback?.({
        pageRecords,
        downloadedCount: args.records.length,
        totalCount: totalCount,
      });
      if (pageRecords.length === args.pageSize) {
        queryPage({
          promiseResolve: args.promiseResolve,
          promiseReject: args.promiseReject,
          store: args.store,
          modelName: args.modelName,
          queryOptions: args.queryOptions,
          records: args.records,
          page: args.page + 1,
          pageSize: args.pageSize,
          pageCallback: args.pageCallback,
        });
      } else {
        args.promiseResolve(args.records);
      }
    },
    (error) => {
      args.promiseReject(error);
    },
  );
}

function cleanupQueryCache(cachePrefix: string) {
  try {
    const removeKeys = Object.keys(localStorage)
      .filter((key) => key && key.startsWith(cachePrefix));

    removeKeys.forEach((key) => {
      try {
        localStorage.removeItem(key!);
      } catch {
        // do nothing
      }
    });
  } catch {
    // do nothing
  }
}

export function updateQueryCache<K extends keyof ModelRegistry>(
  modelName: K,
  cacheKey: string,
  records: ModelRegistry[K][],
) {
  memoryCache[cacheKey] = JSON.stringify(
    records.map((r) => {
      const serialized = r.serialize({ includeId: true });
      serialized.data.type = modelName;

      return serialized;
    }),
  );

  try {
    // Update local storage only if the cache key exists
    if (cacheKey in localStorage) {
      localStorage.setItem(cacheKey, memoryCache[cacheKey]);
    }
  } catch {
    // do nothing
  }
}
